import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Loader2, ShieldOff } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { VoltEmpty } from "../ui/VoltEmpty";
import { VoltFilterBar, type VoltFilter } from "../ui/VoltFilterBar";
import { VoltPageHeader } from "../ui/VoltPageHeader";
import { VoltApiError } from "../voltApi";
import { useWorkshopAccess } from "../workshop/useWorkshopAccess";
import { WorkshopError } from "../workshop/WorkshopError";
import { errorText } from "../workshop/workshopApi";
import { WorkshopTabs } from "../workshop/WorkshopTabs";
import { IdeaCardView } from "./IdeaCardView";
import { ideasApi } from "./ideasApi";
import {
  ALL_DECISIONS,
  BUNDLE_PARAM,
  IDEA_FILTERS,
  ideaQuery,
  SEARCH_PARAM,
  SORT_PARAM,
  stillMatches,
  valueLabel,
} from "./ideaText";
import type { IdeaCard, IdeaChange, IdeaPage } from "./types";

const PAGE = 50;
const MAX_LIMIT = 500;
type Load = "new" | "more" | "refresh";
type Head = Omit<IdeaPage, "karten" | "facetten">;

export const IdeasPage = () => {
  const access = useWorkshopAccess();
  const [granted, setGranted] = useState<boolean | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (access !== "granted") return;
    let live = true;
    ideasApi
      .access()
      .then(() => live && setGranted(true))
      .catch((e) => {
        if (!live) return;
        if (e instanceof VoltApiError && e.status === 403) setDenied(e.message);
        else setError(errorText(e));
      });
    return () => {
      live = false;
    };
  }, [access]);

  if (access === "denied" || denied !== null) {
    return (
      <VoltEmpty
        icon={ShieldOff}
        title="Kein Zugang zur Ideenliste"
        description={
          denied ??
          "Die Ideenliste ist für alle mit Werkstatt-Zugang freigeschaltet. Deinem Konto fehlt die BV-Rolle."
        }
      />
    );
  }
  if (error) return <WorkshopError>{error}</WorkshopError>;
  if (!granted) {
    return (
      <div className="flex flex-col gap-2" aria-busy>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  return <IdeaList />;
};

const IdeaList = () => {
  const [params, setParams] = useSearchParams();
  const values = useMemo(
    () =>
      Object.fromEntries(
        [
          SEARCH_PARAM,
          SORT_PARAM,
          BUNDLE_PARAM,
          ...IDEA_FILTERS.map((f) => f.param),
        ].map((n) => [n, params.get(n) ?? ""]),
      ),
    [params],
  );
  // The search box writes the URL per keystroke; the list follows after a pause.
  const [search, setSearch] = useState(values[SEARCH_PARAM].trim());
  useEffect(() => {
    const next = values[SEARCH_PARAM].trim();
    const t = setTimeout(() => setSearch(next), 400);
    return () => clearTimeout(t);
  }, [values]);
  const queryKey = JSON.stringify(
    ideaQuery({ ...values, [SEARCH_PARAM]: search }),
  );
  const query = useMemo(
    () => JSON.parse(queryKey) as Record<string, string>,
    [queryKey],
  );

  const [cards, setCards] = useState<IdeaCard[]>([]);
  const [head, setHead] = useState<Head | null>(null);
  const [facets, setFacets] = useState<IdeaPage["facetten"]>(null);
  const [loading, setLoading] = useState<Load | null>(null);
  const [atEnd, setAtEnd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  // Offset of the last "more" page; if it brought no new card, auto-loading stops there.
  const [triedAt, setTriedAt] = useState(-1);
  // Only the newest request may write the list; older answers are dropped.
  const run = useRef(0);
  const observer = useRef<IntersectionObserver | null>(null);
  const loaded = cards.filter((k) => stillMatches(k, query)).length;

  const fetchPage = useCallback(
    async (kind: Load, offset: number, limit: number) => {
      const nr = ++run.current;
      setLoading(kind);
      if (kind === "new") setError(null);
      setBroken(false);
      try {
        const { karten, facetten, ...rest } = await ideasApi.list(
          query,
          offset,
          limit,
        );
        if (nr !== run.current) return;
        if (kind !== "more") savedCards.current.clear();
        setCards((old) => {
          if (kind !== "more") return karten;
          const seen = new Set(old.map((k) => k.id));
          return [...old, ...karten.filter((k) => !seen.has(k.id))];
        });
        setHead((old) => ({
          ...rest,
          gesamt: rest.gesamt ?? old?.gesamt ?? 0,
        }));
        if (facetten) setFacets(facetten);
        setTriedAt(kind === "more" ? offset : -1);
      } catch (e) {
        if (nr === run.current) {
          setError(errorText(e));
          setBroken(true);
        }
      } finally {
        if (nr === run.current) setLoading(null);
      }
    },
    [query],
  );

  useEffect(() => {
    void fetchPage("new", 0, PAGE);
  }, [fetchPage]);

  /** Bottom marker; once it nears the viewport the next portion loads. */
  const endRef = useCallback((el: HTMLDivElement | null) => {
    observer.current?.disconnect();
    if (!el || typeof IntersectionObserver === "undefined") return;
    observer.current = new IntersectionObserver(
      ([e]) => setAtEnd(e.isIntersecting),
      { rootMargin: "0px 0px 800px 0px" },
    );
    observer.current.observe(el);
  }, []);

  useEffect(() => {
    if (atEnd && head?.weitere && !loading && !broken && loaded !== triedAt)
      void fetchPage("more", loaded, PAGE);
  }, [atEnd, head, loading, broken, loaded, triedAt, fetchPage]);

  /** Reloads what is on screen, so the scroll position stays. */
  const refresh = () =>
    fetchPage("refresh", 0, Math.min(MAX_LIMIT, Math.max(PAGE, loaded)));

  // Calls per card run one after another; each uses the version the previous one returned.
  const queues = useRef(new Map<number, Promise<void>>());
  // Last saved state per card; a queued change reads it, not the card it was clicked on.
  const savedCards = useRef(new Map<number, IdeaCard>());
  const generations = useRef(new Map<number, number>());

  const enqueue = (id: number, job: () => Promise<void>) => {
    const gen = generations.current.get(id) ?? 0;
    const next = (queues.current.get(id) ?? Promise.resolve()).then(() =>
      gen === (generations.current.get(id) ?? 0) ? job() : undefined,
    );
    queues.current.set(id, next);
    return next;
  };

  /** A failed call drops what was queued behind it and forgets the saved version. */
  const failed = (id: number) => {
    generations.current.set(id, (generations.current.get(id) ?? 0) + 1);
    savedCards.current.delete(id);
  };

  const change = (k: IdeaCard, c: IdeaChange | ((k: IdeaCard) => IdeaChange)) =>
    enqueue(k.id, async () => {
      try {
        const base = savedCards.current.get(k.id) ?? k;
        const body = typeof c === "function" ? c(base) : c;
        const saved = await ideasApi.change(k.id, base.version, body);
        savedCards.current.set(k.id, { ...base, ...saved });
        setCards((ks) =>
          ks.map((x) => (x.id === k.id ? { ...x, ...saved } : x)),
        );
      } catch (e) {
        failed(k.id);
        if (e instanceof VoltApiError && e.status === 409) {
          setError(
            "Jemand hat diese Karte gerade geändert – Liste neu geladen.",
          );
          await refresh();
        } else setError(errorText(e));
      }
    });

  const handover = (k: IdeaCard) =>
    enqueue(k.id, async () => {
      try {
        const base = savedCards.current.get(k.id) ?? k;
        const r = await ideasApi.toWorkshop(k.id, base.version);
        const handed = {
          entscheidung: "werkstatt" as const,
          entwurf_id: r.entwurf_id,
          version: r.version,
          entwurf_link: r.link,
        };
        savedCards.current.set(k.id, { ...base, ...handed });
        setCards((ks) =>
          ks.map((x) => (x.id === k.id ? { ...x, ...handed } : x)),
        );
      } catch (e) {
        failed(k.id);
        setError(errorText(e));
        if (e instanceof VoltApiError && e.status === 409) await refresh();
      }
    });

  // A bundle shows all its members whatever their decision; leaving it returns to the open list.
  const setBundle = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("decision");
        if (id) {
          next.set(BUNDLE_PARAM, id);
          next.set("decision", ALL_DECISIONS);
        } else next.delete(BUNDLE_PARAM);
        return next;
      },
      { replace: true },
    );

  const filters: VoltFilter[] = [
    ...IDEA_FILTERS.map((f) => {
      const counts = facets?.[f.api] ?? {};
      const current = values[f.param];
      const entries = Object.entries(counts)
        .filter(([v]) => !(f.param === "decision" && v === "offen"))
        .sort(([a], [b]) => a.localeCompare(b, "de"))
        .map(([v, n]) => ({
          value: v,
          label: `${valueLabel(f.api, v)} (${n})`,
        }));
      // Keeps a value from the URL selectable before the counts arrive.
      if (current && current !== ALL_DECISIONS && counts[current] === undefined)
        entries.unshift({ value: current, label: valueLabel(f.api, current) });
      if (f.param !== "decision")
        return { param: f.param, label: f.label, options: entries };
      const open = counts.offen;
      return {
        param: f.param,
        label: f.label,
        allLabel: `Entscheidung: offen${open !== undefined ? ` (${open})` : ""}`,
        options: [
          { value: ALL_DECISIONS, label: "Entscheidung: alle" },
          ...entries,
        ],
      };
    }),
    {
      param: SORT_PARAM,
      label: "Sortierung",
      allLabel: search ? "Passt am besten" : "Score (höchster zuerst)",
      options: [
        ...(search
          ? [{ value: "score", label: "Score (höchster zuerst)" }]
          : []),
        { value: "datum", label: "Erstellt (neueste zuerst)" },
      ],
    },
  ];

  const busy = loading === "new" || loading === "refresh" || (!head && !error);
  return (
    <div className="flex flex-col gap-4">
      <WorkshopTabs />
      <VoltPageHeader
        title="Ideen für Anträge"
        description="Freitagsliste · vertraulich"
      />
      <VoltFilterBar
        filters={filters}
        searchPlaceholder="Thema, Ort oder Stichwort, z. B. Spielplatz im Quartier"
        className="mb-0"
      />
      {error && <WorkshopError>{error}</WorkshopError>}
      <p
        className="flex min-h-6 items-center gap-2 text-sm text-muted-foreground"
        aria-live="polite"
      >
        {busy ? (
          <>
            <Loader2 aria-hidden className="size-4 animate-spin" />
            {search && loading === "new" ? "Sucht …" : "Lädt …"}
          </>
        ) : (
          head && (
            <>
              {head.gesamt} {head.gesamt === 1 ? "Karte" : "Karten"}
              {values[BUNDLE_PARAM] && (
                <>
                  {" "}
                  im Bündel ähnlicher Beiträge ·{" "}
                  <button
                    type="button"
                    onClick={() => setBundle(null)}
                    className="font-medium text-primary underline"
                  >
                    zurück zur Liste
                  </button>
                </>
              )}
            </>
          )
        )}
      </p>
      <div
        className={cn(
          "transition-opacity",
          loading === "new" && "pointer-events-none opacity-50",
        )}
      >
        {head && cards.length === 0 && loading !== "new" && (
          <p className="text-sm text-muted-foreground">
            {search
              ? `Keine Treffer für „${search}“ mit diesen Filtern.`
              : "Keine Karten für diese Filter."}
          </p>
        )}
        <div className="flex flex-col gap-3">
          {cards.map((k) => (
            <IdeaCardView
              key={k.id}
              card={k}
              onChange={change}
              onHandover={handover}
              onBundle={
                values[BUNDLE_PARAM] ? undefined : (id) => setBundle(String(id))
              }
            />
          ))}
        </div>
      </div>
      <div
        ref={endRef}
        className="flex min-h-16 items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
      >
        {loading === "more" && (
          <>
            <Loader2 aria-hidden className="size-4 animate-spin" />
            Lädt weitere …
          </>
        )}
        {!loading && broken && head?.weitere && (
          <button
            type="button"
            onClick={() => void fetchPage("more", loaded, PAGE)}
            className="font-medium text-primary underline"
          >
            Weitere laden
          </button>
        )}
        {!loading && head && !head.weitere && cards.length > 0 && (
          <span>Alle {cards.length} geladen</span>
        )}
      </div>
    </div>
  );
};
